import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
} from "@arcadeai/design-system/rhf";
import { Eye, EyeOff, InfoIcon, PlusCircle, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FieldProps, Server } from "@/app/servers/types";
import { getAllSecretReferencesFromHeaders } from "./secret-utils";

type HeaderSecretsFieldProps = FieldProps & {
  server?: Server;
  usedSecrets?: Set<string>;
};

const transformParams = (
  params: Array<{ name: string; value: string }>
): Record<string, string> =>
  params.reduce(
    (acc, param) => {
      if (param.name.trim()) {
        acc[param.name] = param.value;
      }
      return acc;
    },
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    {} as Record<string, string>
  );

const transformRecordToParams = (
  record: Record<string, string | { exists: boolean }>
): Array<{
  id: string;
  name: string;
  value: string;
  exists?: boolean;
}> =>
  Object.entries(record).map(([name, value]) => ({
    id: crypto.randomUUID(),
    name,
    value: typeof value === "string" ? value : "",
    exists: typeof value === "object" ? value.exists : undefined,
  }));

export const HeaderSecretsField = ({
  form,
  canUpdate,
  server,
  usedSecrets: usedSecretsProp,
}: HeaderSecretsFieldProps) => {
  const [secrets, setSecrets] = useState<
    Array<{
      id: string;
      name: string;
      value: string;
      exists?: boolean;
    }>
  >([]);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const isInitialized = useRef(false);
  const isInternalUpdate = useRef(false);

  const value = form.watch("mcp.secrets") || {};
  const headers = form.watch("mcp.headers") || {};
  const formErrors = form.formState.errors;

  const serverSecrets =
    server?.type === "mcp" && server.mcp?.secrets
      ? server.mcp.secrets
      : undefined;

  // Get secrets that are currently being used (fallback to committed headers if prop not provided)
  const usedSecrets =
    usedSecretsProp ?? getAllSecretReferencesFromHeaders(headers);

  const isSecretInUse = useCallback(
    (secretName: string) => usedSecrets.has(secretName),
    [usedSecrets]
  );

  useEffect(() => {
    if (!isInitialized.current) {
      const initialSecrets = serverSecrets
        ? transformRecordToParams(serverSecrets)
        : transformRecordToParams(value);
      setSecrets(initialSecrets);
      isInitialized.current = true;
    } else if (!isInternalUpdate.current) {
      const currentRecord = transformParams(secrets);
      const valueKeys = Object.keys(value).sort();
      const currentKeys = Object.keys(currentRecord).sort();

      if (
        valueKeys.length !== currentKeys.length ||
        valueKeys.some((key, index) => key !== currentKeys[index]) ||
        valueKeys.some((key) => value[key] !== currentRecord[key])
      ) {
        const newSecrets = serverSecrets
          ? transformRecordToParams(serverSecrets)
          : transformRecordToParams(value);
        setSecrets(newSecrets);
      }
    }

    isInternalUpdate.current = false;
  }, [value, secrets, serverSecrets]);

  const updateRecord = useCallback(
    (
      updatedSecrets: Array<{
        id: string;
        name: string;
        value: string;
        exists?: boolean;
      }>
    ) => {
      isInitialized.current = true;
      const record = transformParams(updatedSecrets);
      form.setValue("mcp.secrets", record);
    },
    [form]
  );

  const handleAddItem = useCallback(() => {
    const newSecrets = [
      ...secrets,
      {
        id: crypto.randomUUID(),
        name: "",
        value: "",
        exists: undefined,
      },
    ];
    setSecrets(newSecrets);
  }, [secrets]);

  const handleRemoveItem = useCallback(
    (id: string) => {
      const newSecrets = secrets.filter((secret) => secret.id !== id);
      setSecrets(newSecrets);
      updateRecord(newSecrets);
    },
    [secrets, updateRecord]
  );

  const handleSecretChange = useCallback(
    (id: string, field: "name" | "value", newValue: string) => {
      const updatedSecrets = secrets.map((secret) =>
        secret.id === id ? { ...secret, [field]: newValue } : secret
      );

      setSecrets(updatedSecrets);
      updateRecord(updatedSecrets);
    },
    [secrets, updateRecord]
  );

  const toggleShowSecret = useCallback((id: string) => {
    setShowSecrets((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FormLabel className="font-medium text-sm">
            Header Secrets{" "}
            <span className="text-muted-foreground text-xs">(Optional)</span>
          </FormLabel>
          <Tooltip>
            <TooltipTrigger type="button">
              <InfoIcon className="size-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="w-auto max-w-md" side="top">
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs">
                  Example: Add a secret named <code>API_TOKEN</code>, then use
                  it in headers as{" "}
                  <code>Bearer $&#123;secret:API_TOKEN&#125;</code>
                </p>
                <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1.5">
                  <p className="text-amber-200 text-xs">
                    <span className="font-semibold">Warning:</span> Using a
                    single bearer token or API key means all agents will share
                    these credentials.
                  </p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        {!!canUpdate && (
          <Button
            className="flex items-center gap-x-1.5"
            onClick={handleAddItem}
            size="sm"
            type="button"
            variant="outline"
          >
            <PlusCircle className="h-4 w-4 text-green-500" />
            Add Secret
          </Button>
        )}
      </div>
      <FormDescription>
        Store sensitive values like API tokens or passwords. Reference them in
        headers using <code className="text-xs">$&#123;secret:NAME&#125;</code>
      </FormDescription>
      <div className="space-y-3">
        {secrets.map((item, index) => (
          <div className="space-y-2" key={item.id}>
            {!!item.exists && (
              <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  <span className="font-medium text-xs">A secret is set</span>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <FormItem className="flex-1">
                <FormControl>
                  <div className="relative">
                    <Input
                      autoFocus={index === secrets.length - 1}
                      disabled={!canUpdate}
                      onChange={(e) =>
                        handleSecretChange(item.id, "name", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Tab" && !e.shiftKey) {
                          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                          const el = document.getElementById(
                            `secret-value-${item.id}`
                          ) as HTMLInputElement | null;
                          if (el) {
                            e.preventDefault();
                            el.focus();
                          }
                        }
                      }}
                      placeholder="e.g., API_TOKEN"
                      value={item.name}
                    />
                    {item.name && !isSecretInUse(item.name) && (
                      <div className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2">
                        <Badge
                          className="border-amber-500/50 bg-amber-500/10 text-amber-600 text-xs leading-none dark:text-amber-400"
                          variant="outline"
                        >
                          Unused
                        </Badge>
                      </div>
                    )}
                  </div>
                </FormControl>
              </FormItem>
              <FormItem className="flex-1">
                <FormControl>
                  <div className="relative">
                    <Input
                      className={`pr-10 ${formErrors.mcp?.secrets ? "border-destructive" : ""}`}
                      disabled={!canUpdate}
                      id={`secret-value-${item.id}`}
                      onChange={(e) =>
                        handleSecretChange(item.id, "value", e.target.value)
                      }
                      placeholder={
                        item.exists
                          ? "Leave empty to keep current"
                          : "Your sensitive value"
                      }
                      type={showSecrets[item.id] ? "text" : "password"}
                      value={item.value}
                    />
                    <Button
                      className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => toggleShowSecret(item.id)}
                      size="sm"
                      type="button"
                      variant="link"
                    >
                      {showSecrets[item.id] ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
              {!!canUpdate && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger render={<div />}>
                      <Button
                        className="shrink-0 text-destructive hover:text-destructive/80"
                        disabled={isSecretInUse(item.name)}
                        onClick={() => handleRemoveItem(item.id)}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove Secret</span>
                      </Button>
                    </TooltipTrigger>
                    {isSecretInUse(item.name) && (
                      <TooltipContent>
                        <p>
                          Cannot delete: This secret is being used in headers
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        ))}
        {!!formErrors.mcp?.secrets?.message && (
          <p className="text-destructive text-xs">
            {String(formErrors.mcp.secrets.message)}
          </p>
        )}
      </div>
    </div>
  );
};
